
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {FiltrationOfNullable} from './FiltrationOfNullable';
import {FieldModelFiltrationOfNullable} from './FiltrationOfNullable';

/**
 * FilterUint16 struct
 */
class FilterUint16 {
  /**
   * Initialize struct
   * @param {number=} from
   * @param {number=} to
   * @param {!Set=} exactValues
   * @param {FiltrationOfNullable=} filtrationOfNullable
   * @constructor
   */
  constructor (from = null, to = null, exactValues = new Set(), filtrationOfNullable = null) {
    this.From = from
    this.To = to
    this.ExactValues = exactValues
    this.FiltrationOfNullable = filtrationOfNullable
  }

  /**
   * Copy struct (shallow copy)
   * @this {!FilterUint16}
   * @param {!FilterUint16} other Other struct
   * @returns {!FilterUint16} This struct
   */
  copy (other) {
    if (other.From != null) {
      this.From = other.From
    } else {
      this.From = null
    }
    if (other.To != null) {
      this.To = other.To
    } else {
      this.To = null
    }
    if (other.ExactValues != null) {
      this.ExactValues = new Set()
      for (let item of other.ExactValues) {
        if (item != null) {
          let tempItem
          tempItem = item
          this.ExactValues.add(tempItem)
        } else {
          this.ExactValues.add(null)
        }
      }
    } else {
      this.ExactValues = null
    }
    if (other.FiltrationOfNullable != null) {
      this.FiltrationOfNullable = FiltrationOfNullable.fromObject(other.FiltrationOfNullable)
    } else {
      this.FiltrationOfNullable = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!FilterUint16}
   * @returns {!FilterUint16} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new FilterUint16Model(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new FilterUint16Model(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!FilterUint16}
   * @param {!FilterUint16} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof FilterUint16)) {
      throw new TypeError('Instance of FilterUint16 is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!FilterUint16}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      From: ((this.From != null) ? this.From : null),
      To: ((this.To != null) ? this.To : null),
      ExactValues: ((this.ExactValues != null) ? Array.from(this.ExactValues, item => ((item != null) ? item : null)) : null),
      FiltrationOfNullable: ((this.FiltrationOfNullable != null) ? this.FiltrationOfNullable : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return FilterUint16.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!FilterUint16} other Object value
   * @returns {!FilterUint16} Created struct
   */
  static fromObject (other) {
    return new FilterUint16().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!FilterUint16}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return FilterUint16.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!FilterUint16}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 104
  }
}

export { FilterUint16 };

/**
 * Fast Binary Encoding FilterUint16 field model
 */
class FieldModelFilterUint16 extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.From = new fbe.FieldModelOptional(new fbe.FieldModelUInt16(buffer, 4 + 4), buffer, 4 + 4)
    this.To = new fbe.FieldModelOptional(new fbe.FieldModelUInt16(buffer, this.From.fbeOffset + this.From.fbeSize), buffer, this.From.fbeOffset + this.From.fbeSize)
    this.ExactValues = new fbe.FieldModelSet(new fbe.FieldModelUInt16(buffer, this.To.fbeOffset + this.To.fbeSize), buffer, this.To.fbeOffset + this.To.fbeSize)
    this.FiltrationOfNullable = new fbe.FieldModelOptional(new FieldModelFiltrationOfNullable(buffer, this.ExactValues.fbeOffset + this.ExactValues.fbeSize), buffer, this.ExactValues.fbeOffset + this.ExactValues.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelFilterUint16}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelFilterUint16}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.From.fbeSize + this.To.fbeSize + this.ExactValues.fbeSize + this.FiltrationOfNullable.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelFilterUint16}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.From.fbeExtra + this.To.fbeExtra + this.ExactValues.fbeExtra + this.FiltrationOfNullable.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelFilterUint16}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelFilterUint16.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelFilterUint16}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 104
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelFilterUint16}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelFilterUint16}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.From.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.From.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.From.fbeSize

    if ((fbeCurrentSize + this.To.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.To.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.To.fbeSize

    if ((fbeCurrentSize + this.ExactValues.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ExactValues.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ExactValues.fbeSize

    if ((fbeCurrentSize + this.FiltrationOfNullable.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.FiltrationOfNullable.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.FiltrationOfNullable.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelFilterUint16}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelFilterUint16}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelFilterUint16}
   * @param {!FilterUint16} fbeValue Default value, defaults is new FilterUint16()
   * @returns {!FilterUint16} FilterUint16 value
   */
  get (fbeValue = new FilterUint16()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelFilterUint16}
   * @param {!FilterUint16} fbeValue FilterUint16 value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.From.fbeSize) <= fbeStructSize) {
      fbeValue.From = this.From.get()
    } else {
      fbeValue.From = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.From.fbeSize

    if ((fbeCurrentSize + this.To.fbeSize) <= fbeStructSize) {
      fbeValue.To = this.To.get()
    } else {
      fbeValue.To = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.To.fbeSize

    if ((fbeCurrentSize + this.ExactValues.fbeSize) <= fbeStructSize) {
      this.ExactValues.get(fbeValue.ExactValues)
    } else {
      fbeValue.ExactValues.clear()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ExactValues.fbeSize

    if ((fbeCurrentSize + this.FiltrationOfNullable.fbeSize) <= fbeStructSize) {
      fbeValue.FiltrationOfNullable = this.FiltrationOfNullable.get()
    } else {
      fbeValue.FiltrationOfNullable = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.FiltrationOfNullable.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelFilterUint16}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelFilterUint16}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelFilterUint16}
   * @param {!FilterUint16} fbeValue FilterUint16 value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelFilterUint16}
   * @param {FilterUint16} fbeValue FilterUint16 value
   */
  setFields (fbeValue) {
    this.From.set(fbeValue.From)
    this.To.set(fbeValue.To)
    this.ExactValues.set(fbeValue.ExactValues)
    this.FiltrationOfNullable.set(fbeValue.FiltrationOfNullable)
  }
}

export { FieldModelFilterUint16 };

/**
 * Fast Binary Encoding FilterUint16 model
 */
class FilterUint16Model extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelFilterUint16(this.buffer, 4)
  }

  /**
   * Get the FilterUint16 model
   * @this {!FilterUint16Model}
   * @returns {!FieldModelFilterUint16} model FilterUint16 model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!FilterUint16Model}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!FilterUint16Model}
   * @returns {!number} Model type
   */
  get fbeType () {
    return FilterUint16Model.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!FilterUint16Model}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelFilterUint16.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!FilterUint16Model}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!FilterUint16Model}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!FilterUint16Model}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!FilterUint16Model}
   * @param {!FilterUint16} value FilterUint16 value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!FilterUint16Model}
   * @param {!FilterUint16} value FilterUint16 value, defaults is new FilterUint16()
   * @return {!object} Deserialized FilterUint16 value and its size
   */
  deserialize (value = new FilterUint16()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new FilterUint16(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new FilterUint16(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!FilterUint16Model}
   * @param {!number} prev Previous FilterUint16 model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { FilterUint16Model };
